import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { ArrowUpRight } from 'react-bootstrap-icons';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

interface Step2VerifyCodeProps {
  email: string;
  onComplete: () => void;
  onBack: () => void;
}

const Step2VerifyCode: React.FC<Step2VerifyCodeProps> = ({ email, onComplete, onBack }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { currentUser } = useAuth();

  useEffect(() => {
    inputRefs.current[0]?.focus();

    const timer = setInterval(() => {
      setResendTimer(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleCodeChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setCodeError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newCode.every(digit => digit !== '')) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '').slice(0, 6).split('');
    
    const newCode = [...code];
    digits.forEach((digit, i) => {
      if (i < 6) newCode[i] = digit;
    });
    setCode(newCode);

    const nextEmptyIndex = newCode.findIndex(d => d === '');
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[5]?.focus();
      handleVerify(newCode.join(''));
    }
  };

  const handleVerify = async (verificationCode?: string) => {
    const codeToVerify = verificationCode || code.join('');

    if (codeToVerify.length !== 6) {
      setCodeError('Please enter all 6 digits');
      return;
    }

    setError('');
    setCodeError('');
    setLoading(true);

    try {
      const payload: any = {
        email,
        code: codeToVerify
      };
      
      if (currentUser?.uid) {
        payload.uid = currentUser.uid;
      }
      
      await api.post('/api/register/verify-code', payload);

      onComplete();
    } catch (err: any) {
      if (err.response?.data?.error) {
        setCodeError(err.response.data.error);
      } else {
        setCodeError('Invalid or expired code. Please try again.');
      }
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;

    setError('');
    setLoading(true);

    try {
      const response = await api.post<{message: string; email: string; code?: string}>('/api/register/send-code', { email });
      
      if (response.code) {
        console.log('New verification code (dev only):', response.code);
      }

      setResendTimer(60);
      setError('');
      alert('A new code has been sent to your email');
    } catch (err: any) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h1 className="text-center">We've emailed you a code</h1>
      <h2 className="text-center text-muted mb-4">To continue account setup, enter the code we've sent to {email}.</h2>

      {error && <Alert variant="danger">{error}</Alert>}
      {codeError && <div className="text-danger text-center mb-3">{codeError}</div>}

      <Form onSubmit={(e) => { e.preventDefault(); handleVerify(); }} noValidate>
        <div className="verification-code-row">
          {code.map((digit, index) => (
            <div key={index} className="verification-code-input-container">
              <Form.Control
                ref={(el: any) => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className={`text-center input-digit verification-code-input ${codeError ? 'is-invalid' : ''}`}
              />
              {!digit && (
                <div className="verification-code-placeholder" />
              )}
            </div>
          ))}
        </div>

        <Button 
          type="submit" 
          className="w-100 mb-3"
          disabled={loading || code.some(d => d === '')}
        >
          {loading ? 'VERIFYING...' : 'VERIFY'}
        </Button>

        <div className="text-center mb-3">
          <small className="text-muted">
            Didn't receive a code?{' '}
            {resendTimer > 0 ? (
              <span>Resend code in {resendTimer}s</span>
            ) : (
              <Button 
                variant="link" 
                size="sm" 
                onClick={handleResend}
                disabled={loading}
              >
                Resend code <ArrowUpRight className="me-2" />
              </Button>
            )}
          </small>
        </div>

        <Button 
          variant="link" 
          className="w-100 text-decoration-none"
          onClick={onBack}
        >
          BACK
        </Button>
      </Form>
    </>
  );
};

export default Step2VerifyCode;